import type { Game } from "@rally/rating";
import { DemoError, demoMatches } from "./demo.js";
import { SPORT } from "./sport.js";
import { supabase } from "./supabase.js";
import type {
  Kind,
  MatchParticipant,
  MatchContext,
  MatchStatus,
  MatchSummary,
  Sport,
  MatchCategory,
} from "./types.js";

export interface LogMatchInput {
  kind: Kind;
  games: Game[];
  sideA: string[];
  sideB: string[];
  playedAt?: string;
}

export interface MatchRow {
  id: string;
  sport: Sport;
  kind: Kind;
  category: MatchCategory | null;
  status: MatchStatus;
  games: Game[];
  winner: "A" | "B";
  played_at: string;
  entered_by: string;
  context: MatchContext;
  moves_ratings: boolean;
  events: { id: string; name: string } | null;
  match_participants: {
    member_id: string;
    side: "A" | "B";
    confirmed_at: string | null;
    members: { display_name: string; avatar_url: string | null } | null;
  }[];
}

// The event comes back embedded rather than as an id to look up: the log is a list of
// twenty-five rows on a screen where the name is the whole point of showing it, and a
// second request per row to find out it was Tuesday's tie would be absurd.
//
// `moves_ratings` is not a column. It is a function over the match row that PostgREST offers
// as one (20260929000200), and it is the same rule confirmation and recompute use — so the
// "unrated" label cannot disagree with what actually happened to the rating.
const MATCH_COLUMNS =
  "id, sport, kind, category, status, games, winner, played_at, entered_by, context, moves_ratings, " +
  "events (id, name), " +
  "match_participants (member_id, side, confirmed_at, members (display_name, avatar_url))";

export function toMatch(row: MatchRow): MatchSummary {
  const participants: MatchParticipant[] = row.match_participants.map((p) => ({
    memberId: p.member_id,
    displayName: p.members?.display_name ?? "Unknown",
    avatarUrl: p.members?.avatar_url ?? null,
    side: p.side,
    confirmedAt: p.confirmed_at,
  }));

  return {
    id: row.id,
    sport: row.sport,
    kind: row.kind,
    category: row.category,
    status: row.status,
    games: row.games,
    winner: row.winner,
    playedAt: row.played_at,
    enteredBy: row.entered_by,
    context: row.context,
    movesRatings: row.moves_ratings,
    event: row.events,
    participants,
  };
}

/**
 * One RPC, one transaction. Two client inserts would leave an orphan match behind
 * whenever the participants insert failed, and a match with no opponents can never be
 * confirmed. The winner is derived server-side from the games.
 */
export async function logMatch(input: LogMatchInput): Promise<string> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase.rpc("log_match", {
    p_sport: SPORT,
    p_kind: input.kind,
    p_games: input.games,
    p_side_a: input.sideA,
    p_side_b: input.sideB,
    p_played_at: input.playedAt ?? new Date().toISOString(),
  });

  if (error) throw new Error(error.message);
  return data as string;
}

/**
 * What to narrow the match log to. Every field is optional and they compose; an empty
 * filter is the whole community's history in this sport.
 */
export interface MatchFilters {
  /** Only matches this member played in. */
  memberId?: string;
  kind?: Kind;
  status?: MatchStatus;
  category?: MatchCategory;
  /** Casual play, or matches that were part of an event. */
  context?: MatchContext;
  /** One event's matches — what the event's own results screen asks for. */
  eventId?: string;
}

export interface MatchPage {
  matches: MatchSummary[];
  /** Whether asking for the next page is worth a request. */
  more: boolean;
}

export const MATCH_PAGE = 25;

/**
 * The match log, most recent first — in this app's sport only. Their pickleball matches
 * belong to the pickleball app; showing them here would put a scoreline next to a rating it
 * has nothing to do with.
 *
 * Every match is readable by every active member (`matches_read`), so this is a community
 * feed rather than a private one. `memberId` narrows it to one person's history and is what
 * the confirmation queue and the profiles ask for.
 *
 * One request. Filtering by member used to read every match id the member appeared in and
 * then ask for those ids, which put the whole of someone's history into a URL: around 37
 * bytes per match, in a query string the gateway stops reading at 8KB. Past roughly 215
 * matches the second request came back 414 and this screen — and the confirmation badge in
 * the shell, which runs on every screen — simply went blank. Four matches a week gets there
 * inside a year.
 *
 * `mine` is an aliased inner join used only as a filter, so its size does not depend on
 * the member's history. The unaliased `match_participants` embed below it is what actually
 * returns the players; filtering the embed you are reading from would narrow it to the one
 * participant that matched and lose everybody else on the court.
 *
 * Paged by `range`, and the page is asked for one row longer than it is: a count would cost
 * an exact scan of the whole log on every screen to answer a question — is there a next
 * page — that one extra row answers.
 */
export async function fetchMatches(
  filters: MatchFilters = {},
  page: { offset?: number; limit?: number } = {},
): Promise<MatchPage> {
  const offset = page.offset ?? 0;
  const limit = page.limit ?? MATCH_PAGE;

  if (!supabase) {
    const all = demoMatches.filter((match) => matchesFilters(match, filters));
    return { matches: all.slice(offset, offset + limit), more: all.length > offset + limit };
  }

  let query = supabase
    .from("matches")
    .select(
      filters.memberId
        ? `${MATCH_COLUMNS}, mine:match_participants!inner (member_id)`
        : MATCH_COLUMNS,
    )
    .eq("sport", SPORT);

  if (filters.memberId) query = query.eq("mine.member_id", filters.memberId);
  if (filters.kind) query = query.eq("kind", filters.kind);
  if (filters.status) query = query.eq("status", filters.status);
  if (filters.category) query = query.eq("category", filters.category);
  if (filters.context) query = query.eq("context", filters.context);
  if (filters.eventId) query = query.eq("event_id", filters.eventId);

  const { data, error } = await query
    .order("played_at", { ascending: false })
    .range(offset, offset + limit);

  if (error) throw new Error(error.message);

  const rows = data as unknown as MatchRow[];
  return { matches: rows.slice(0, limit).map(toMatch), more: rows.length > limit };
}

/** The same filters against sample data, so sample mode's selectors do something. */
function matchesFilters(match: MatchSummary, filters: MatchFilters): boolean {
  if (filters.memberId && !match.participants.some((p) => p.memberId === filters.memberId)) {
    return false;
  }
  if (filters.kind && match.kind !== filters.kind) return false;
  if (filters.status && match.status !== filters.status) return false;
  if (filters.context && match.context !== filters.context) return false;
  if (filters.eventId && match.event?.id !== filters.eventId) return false;
  if (filters.category && match.category !== filters.category) return false;
  return true;
}

/**
 * One member's matches, newest first. The confirmation badge in the shell and the record on
 * a profile both count from this, so it takes a limit rather than a page.
 */
export async function fetchMyMatches(
  memberId: string,
  limit = MATCH_PAGE,
): Promise<MatchSummary[]> {
  const { matches } = await fetchMatches({ memberId }, { limit });
  return matches;
}

/**
 * Confirmation has to come from the other side of the net, so a match is only waiting on
 * you if you are on the opposite side to whoever entered it. The Edge Function enforces
 * the same rule; this keeps the UI from offering a button that would be refused.
 */
export function awaitsConfirmationFrom(match: MatchSummary, memberId: string): boolean {
  if (match.status !== "pending") return false;

  const me = match.participants.find((p) => p.memberId === memberId);
  if (!me || me.confirmedAt) return false;

  const enteredBySide = match.participants.find((p) => p.memberId === match.enteredBy)?.side;
  return me.side !== enteredBySide;
}

export interface ConfirmResult {
  /** False for a mixed match: it settled, and no rating moved. */
  rated: boolean;
  /** Null when nothing was rated — the score share only means anything as a rating input. */
  scoreA: number | null;
  changes: { member_id: string; delta: number }[];
}

/**
 * The only path to a rating write, and it is not this code — the Edge Function holds
 * service_role and does the maths. This just asks.
 */
export async function confirmMatch(matchId: string): Promise<ConfirmResult> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase.functions.invoke("confirm-match", {
    body: { matchId },
  });

  if (error) throw new Error(error.message);
  return data as ConfirmResult;
}

export async function disputeMatch(matchId: string, reason: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("dispute_match", {
    target_match: matchId,
    reason,
  });

  if (error) throw new Error(error.message);
}

/**
 * Cancel a match outright: it should not have happened and should count for nobody.
 *
 * Different from a dispute, which says the score is wrong and asks someone to sort it out.
 * This says there is nothing to sort out. Organisers and admins only — `void_match` enforces
 * that itself, so a player calling this directly is refused by the database rather than by
 * the absence of a button.
 *
 * If the match had already been rated, this drops its rating events but leaves everyone's
 * current rating where it was. `npm run recompute` is what puts those right; the numbers are
 * derived from the match log and the match log has just changed.
 */
export async function voidMatch(matchId: string, reason: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("void_match", {
    target_match: matchId,
    reason,
  });

  if (error) throw new Error(error.message);
}

/**
 * Fix a score that was typed wrong.
 *
 * Not a void: the match happened, and cancelling it to re-enter it would lose that from
 * both players' records. `correct_match` rewrites the games, re-derives the winner from
 * them, and drops the match's rating events — Glicko updates are not cleanly invertible, so
 * the numbers are put right by `npm run recompute` replaying the log forwards, exactly as
 * they are after a void or a dispute.
 *
 * Organisers only, and it is audited with the old score, the new one and the reason.
 */
export async function correctMatch(matchId: string, games: Game[], reason: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("correct_match", {
    target_match: matchId,
    p_games: games,
    reason,
  });

  if (error) throw new Error(error.message);
}

/** `21–18, 19–21, 21–15`, from the perspective of side A. */
export function formatScoreline(games: Game[], perspective: "A" | "B" = "A"): string {
  return games
    .map((game) => (perspective === "A" ? `${game.a}–${game.b}` : `${game.b}–${game.a}`))
    .join("  ");
}

/** One side's people, for avatars. Same ordering as `sideNames`, so they line up. */
export function sideOf(match: MatchSummary, side: "A" | "B") {
  return match.participants
    .filter((p) => p.side === side)
    .map((p) => ({ name: p.displayName, url: p.avatarUrl }));
}

export function sideNames(match: MatchSummary, side: "A" | "B"): string {
  return match.participants
    .filter((p) => p.side === side)
    .map((p) => p.displayName)
    .join(" & ");
}
